// @ts-nocheck
import { getSpreedlyFrame, useSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import { getSpreedlyConfig, SpreedlyConfig } from "@insite/client-framework/Services/SettingsService";
import { generateSpreedlyFrameStyleConfig } from "@insite/mobius/SpreedlyFrame";
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import translate from "@insite/client-framework/Translate";
import { convertSpreedlyCardType } from "@insite/content-library/Widgets/SavedPayments/PaymentUtilities";
import { CreditCardDetailsValidation } from "@store/Overrides/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import { SavedPaymentProfileValidation } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import { CreditCardDto } from "@insite/client-framework/Types/ApiModels";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import loadPaymetricConfig from "@insite/client-framework/Store/Context/Handlers/LoadPaymetricConfig";
import { usePaymetricFrame } from "@insite/client-framework/Common/Hooks/usePaymetricFrame";
import { useDispatch } from "react-redux";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetShipToAndBillTo from "@store/Framework/Store/Data/Accounts/Hooks/useGetShipToAndBillTo";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import usePaymentDetailsFormValidation from "@store/Hooks/Payments/usePaymentDetailsFormValidation";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getInvoicePayments,
    SpreedlyInvoicePayment,
    submitAccountBalancePayment,
    TokenData,
} from "@store/Framework/Services/AccountBalanceService";
import isApiError from "@insite/client-framework/Common/isApiError";
import useHandleApiError from "@store/Framework/Common/Hooks/useHandleApiError";
import useAccountBalanceState from "@store/Framework/Common/Hooks/AccountBalance/useAccountBalanceState";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";

const useAccountBalancePayment = () => {
    const bunzlDispatch = useBunzlDispatch();
    const dispatch = useDispatch();
    const currencyFormatter = useLocalizeCurrency();
    const { billTo: billToState } = useGetShipToAndBillTo();
    const spreedlyFrame = getSpreedlyFrame();
    const settings = useSettingsCollection();
    const bypassCvvForSavedCards = settings?.cartSettings.bypassCvvForSavedCards;
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

    const { accountBalance, isSubmitting } = useAccountBalanceState();
    const amountToPay = accountBalance?.payments.reduce(
        (acc, option) => acc + (option.isEditing ? option.paymentAmount ?? 0 : 0),
        0,
    );
    const formattedAmount = currencyFormatter(amountToPay ?? 0);
    const invoicePaymentOptions =
        accountBalance?.payments.filter(option => option.isEditing && option.paymentAmount) ?? [];
    const paymentMethods = accountBalance?.paymentOptions?.paymentMethods;

    const paymentDetailsFormState = usePaymentDetailsFormState();
    const {
        paymentMethod,
        setPaymentMethod,
        setCardHolderName,
        setCardNumber,
        setCardType,
        expirationMonth,
        setExpirationMonth,
        expirationYear,
        setExpirationYear,
        setSecurityCode,
        useBillingAddress,
        setUseBillingAddress,
        address1,
        setAddress1,
        countryId,
        setCountryId,
        stateId,
        setStateId,
        city,
        setCity,
        postalCode,
        setPostalCode,
        poNumberError,
        countryError,
        stateError,
        cityError,
        paystandError,
        showFormErrors,
        setShowFormErrors,
        setIsCardNumberTokenized,
        creditCardLastFourDigits,
        bankAccountLastFourDigits,
        isIframeLoaded,
        paymentMethodRef,
        poNumberRef,
        address1Ref,
        address1Error,
        postalCodeError,
        setSaveCard,
        saveCard,
        countryRef,
        stateRef,
        cityRef,
        postalCodeRef,
        setMaskedCardNumber,
    } = paymentDetailsFormState;

    const countries = useAppSelector(getCurrentCountries);
    useEffect(() => {
        if (!countries) {
            dispatch(loadCurrentCountries());
        }
    }, [countries]);
    const handleApiError = useHandleApiError();
    const selectedCountry = countries?.find(country => country.id === countryId);

    const {
        validateAddress1,
        validateCity,
        validateCountry,
        validatePaymentMethod,
        validatePostalCode,
        validateState,
    } = usePaymentDetailsFormValidation(paymentDetailsFormState, {
        paymentMethods,
        isCreditCard: true,
        isECheck: false,
        selectedCountry: undefined,
        showPoNumber: false,
        requiresPoNumber: false,
    });

    const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPaymentMethod(event.currentTarget.value);
    };
    const creditCardDetails = useRef<CreditCardDetailsValidation>(null);

    const usePaymetricGateway = settings?.websiteSettings.usePaymetricGateway;
    const paymetricConfig = useAppSelector(state => state.context.paymetricConfig);
    const isCreditCard = true;
    const [paymetricFrameRef, paymetricIframe] = usePaymetricFrame(
        usePaymetricGateway,
        paymentMethod,
        isCreditCard,
        paymetricConfig,
        loadPaymetricConfig,
    );

    const theme = useTheme();
    const isLoading = useAppSelector(state => toToolkitApplicationState(state).bunzl.pages.accountBalance.isLoading);
    const setIsLoading = (isLoading: boolean) =>
        bunzlDispatch({ type: "Pages/AccountBalance/SetIsLoading", isLoading });
    const spreedlyFrameStyleConfig = generateSpreedlyFrameStyleConfig({ theme });
    const [spreedlyConfig, setSpreedlyConfig] = React.useState<SpreedlyConfig | undefined>(undefined);
    const useAydenDropIn = false;
    const paymentOptions = accountBalance?.paymentOptions;
    const handleSaveSubmit = async () => {
        setIsLoading(true);
        const fullSpreedlyPayment: SpreedlyInvoicePayment = {
            creditCard: undefined,
            amount: amountToPay ?? 0,
            paymentOptions: invoicePaymentOptions,
            storePaymentProfile: false,
            isPaymentProfile: true,
            browserInfo: spreedlyFrame.getBrowserInfo(),
            paymentProfileId: currentPaymentMethodDto?.name,
        };
        handleApiError(() => handleSubmit(fullSpreedlyPayment));
    };
    const handleSubmit = async (invoicePayment: SpreedlyInvoicePayment) => {
        setIsLoading(true);
        try {
            const result = await submitAccountBalancePayment(invoicePayment);
            bunzlDispatch({ type: "Pages/AccountBalance/ToggleSubmitting" });
            if (result.updateErpSuccess) {
                bunzlDispatch({
                    type: "Pages/AccountBalance/SetAccountBalanceMessage",
                    accountMessage: {
                        message: String(siteMessage("AccountBalance_PaymentSuccess", result.transactionNumber)),
                        type: "success",
                    },
                });
            } else {
                bunzlDispatch({
                    type: "Pages/AccountBalance/SetAccountBalanceMessage",
                    accountMessage: {
                        message: String(siteMessage("AccountBalance_ErpError", result.transactionNumber)),
                        type: "warning",
                    },
                });
            }
            const refreshedPayments = await getInvoicePayments();
            dispatch({
                type: "Pages/AccountBalance/LoadAccountBalance",
                accountBalance: refreshedPayments,
            });
        } catch (error: unknown) {
            if (isApiError(error)) {
                if (error.status === 401 || error.status === 403) {
                    throw error;
                }
                setErrorMessage(error.errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleOnTokenize = async (token: string, tokenData: TokenData): Promise<void> => {
        const creditCardDto: CreditCardDto = {
            cardType: convertSpreedlyCardType(tokenData.card_type),
            cardHolderName: tokenData.full_name,
            expirationMonth: expirationMonth,
            expirationYear: expirationYear,
            maskedCardNumber: `-${tokenData.last_four_digits}`,
            address1: useBillingAddress ? "" : address1,
            cardNumber: token,
            securityCode: "CVV",
            city: useBillingAddress ? "" : city,
            country: useBillingAddress ? "" : selectedCountry?.name ?? "",
            postalCode: useBillingAddress ? "" : postalCode,
            state: useBillingAddress ? "" : selectedCountry?.states?.find(state => state.id === stateId)?.name ?? "",
            countryAbbreviation: useBillingAddress ? "" : selectedCountry?.abbreviation ?? "",
            stateAbbreviation: useBillingAddress
                ? ""
                : selectedCountry?.states?.find(state => state.id === stateId)?.abbreviation ?? "",
            useBillingAddress: useBillingAddress,
            browserInfo: spreedlyFrame.getBrowserInfo(),
        };

        const fullSpreedlyPayment: SpreedlyInvoicePayment = {
            creditCard: creditCardDto,
            amount: amountToPay ?? 0,
            paymentOptions: invoicePaymentOptions,
            storePaymentProfile: saveCard,
            isPaymentProfile: false,
        };
        handleApiError(() => handleSubmit(fullSpreedlyPayment));
    };
    const handleTokenizeRef = useRef(handleOnTokenize);
    handleTokenizeRef.current = handleOnTokenize;

    useEffect(() => {
        const setupSpreedly = async () => {
            const spreedlyConfig = await getSpreedlyConfig();

            setSpreedlyConfig(spreedlyConfig);
        };
        setupSpreedly();
    }, []);
    const savedPaymentProfile = useRef<SavedPaymentProfileValidation>(null);
    const iframeName = "Spreedly";
    useSpreedlyFrame();
    const paymentMethodDtos = accountBalance?.paymentOptions?.paymentMethods;
    const defaultPaymentMethod = paymentMethodDtos?.find(method =>
        method.description.toLowerCase().endsWith(`(${translate("default")})`),
    );
    const currentPaymentMethodDto =
        paymentMethodDtos?.find(method => method.name === paymentMethod) ??
        defaultPaymentMethod ??
        paymentMethodDtos?.[0];
    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validate()) {
            setShowFormErrors(true);
            return false;
        }

        if (currentPaymentMethodDto?.isCreditCard) {
            creditCardDetails.current?.tokenize();
        } else {
            if (currentPaymentMethodDto?.isPaymentProfile && !currentPaymentMethodDto.isPaymentProfileExpired) {
                handleSaveSubmit();
            }
        }
    };

    const validate = () => {
        const paymentMethodValid = validatePaymentMethod(currentPaymentMethodDto?.name ?? "");

        var address1Valid = true;
        var countryValid = true;
        var stateValid = true;
        var cityValid = true;
        var postalCodeValid = true;

        if (!useBillingAddress) {
            // Bunzl -->
            address1Valid = validateAddress1(address1);
            countryValid = validateCountry(countryId);
            stateValid = validateState(stateId);
            cityValid = validateCity(city);
            postalCodeValid = validatePostalCode(postalCode);
        }

        let creditCardValid = true;
        if (creditCardDetails?.current) {
            creditCardValid = creditCardDetails.current.validate();
        }
        if (savedPaymentProfile?.current) {
            creditCardValid = savedPaymentProfile?.current.validate();
        }

        if (!paymentMethodValid) {
            paymentMethodRef.current?.focus();
        } else if (!address1Valid) {
            address1Ref.current?.focus();
        } else if (!countryValid) {
            countryRef.current?.focus();
        } else if (!stateValid) {
            stateRef.current?.focus();
        } else if (!cityValid) {
            cityRef.current?.focus();
        } else if (!postalCodeValid) {
            postalCodeRef.current?.focus();
        }

        return (
            paymentMethodValid &&
            creditCardValid &&
            address1Valid &&
            countryValid &&
            stateValid &&
            cityValid &&
            postalCodeValid
        );
    };

    return {
        errorMessage,
        setErrorMessage,
        paymentMethod,
        setPaymentMethod,
        cardHolderName: paymentDetailsFormState.cardHolderName,
        setCardHolderName,
        cardNumber: paymentDetailsFormState.cardNumber,
        setCardNumber,
        cardType: paymentDetailsFormState.cardType,
        setCardType,
        expirationMonth,
        setExpirationMonth,
        expirationYear,
        setExpirationYear,
        securityCode: paymentDetailsFormState.securityCode,
        setSecurityCode,
        useBillingAddress,
        setUseBillingAddress,
        address1,
        setAddress1,
        countryId,
        setCountryId,
        stateId,
        setStateId,
        city,
        setCity,
        postalCode,
        setPostalCode,
        poNumberError,
        countryError,
        stateError,
        cityError,
        paystandError,
        showFormErrors,
        isCardNumberTokenized: paymentDetailsFormState.isCardNumberTokenized,
        setIsCardNumberTokenized,
        creditCardLastFourDigits,
        bankAccountLastFourDigits,
        isIframeLoaded,
        saveCard,
        setSaveCard,
        maskedCardNumber: paymentDetailsFormState.maskedCardNumber,
        setMaskedCardNumber,
        countries,
        selectedCountry,
        billToState,
        settings,
        currentPaymentMethodDto,
        spreedlyConfig,
        spreedlyFrameStyleConfig,
        isLoading,
        formattedAmount,
        paymentMethods,
        paymentOptions,
        iframeName,
        usePaymetricGateway,
        bypassCvvForSavedCards,
        isSubmitting,
        useAydenDropIn,
        paymentMethodRef,
        poNumberRef,
        address1Ref,
        countryRef,
        stateRef,
        cityRef,
        postalCodeRef,
        creditCardDetails,
        savedPaymentProfile,
        paymetricFrameRef,
        handleTokenizeRef,
        address1Error,
        postalCodeError,
        handlePaymentMethodChange,
        handleFormSubmit,
    };
};

export default useAccountBalancePayment;
